// =============================================================================
// io/sync/feedback.ts — Send feedback, to the same endpoint the old client uses.
// -----------------------------------------------------------------------------
// One POST to `bug-report-submit`, which writes the report, and then — for a
// BUG or a FEATURE REQUEST — opens a GitHub issue on the private tracker
// (D162). The other three types go to the support inbox. None of that is
// decided here; the client sends a type and the function routes it.
//
// ── 🚨 THE CONTEXT IS THE POINT, NOT THE MESSAGE ──
//
// "The chart went blank" is a conversation. The same words plus the tab, the
// app version, the URL and the last few JavaScript errors is a fix. Everything
// in `gatherContext` exists because triage otherwise has to ask for it, and the
// person who hit the bug has usually closed the app by then.
//
// ── ⚠ THE SAME PAYLOAD SHAPE AS THE OLD CLIENT, DELIBERATELY ──
//
// Both clients post to one function against one table. A field renamed here
// would land as `null` in the row and nobody would notice until a report
// arrived with half its context missing. The keys are the function's, verbatim.
// =============================================================================

import type { SupabaseClient } from '@supabase/supabase-js';
import { ENV } from '../../env';
import { recentErrors, type LoggedError } from '../diagnostics/errorLog';

/** The five types, and they must match the function's `VALID_TYPES`. */
export const FEEDBACK_TYPES = [
  {
    id: 'bug', icon: '🐛', label: 'Bug',
    placeholder: 'What went wrong? What were you doing, what did you expect, and what happened instead?',
  },
  {
    id: 'feature_request', icon: '💡', label: 'Feature request',
    placeholder: 'What would you like to be able to do? Describe the workflow or the problem you are trying to solve.',
  },
  {
    id: 'ux_suggestion', icon: '🎨', label: 'UX suggestion',
    placeholder: 'Something feel clunky, confusing, or like it could be smoother? Where did you hit it?',
  },
  {
    id: 'question', icon: '❓', label: 'Question',
    placeholder: 'Ask away — we’ll get back to you.',
  },
  {
    id: 'other', icon: '💬', label: 'Other feedback',
    placeholder: 'Anything on your mind — kudos, complaints, ideas, observations.',
  },
] as const;

export type FeedbackTypeId = (typeof FEEDBACK_TYPES)[number]['id'];

export interface FeedbackContext {
  currentTab: string;
  appVersion: string;
  userAgent: string;
  windowSize: string;
  pageUrl: string;
  recentErrors: LoggedError[];
}

/**
 * Everything that gets attached without the user typing it.
 *
 * ⚠ GATHERED WHEN THE MODAL OPENS, not when Send is pressed, and the difference
 * matters: the errors that caused the bug are the ones already in the buffer.
 * Reading it at submit time would include anything thrown while the person was
 * typing — and on a ten-entry ring, a noisy warning could push the actual cause
 * out before it was ever sent.
 */
export function gatherContext(currentTab: string, appVersion: string): FeedbackContext {
  return {
    currentTab: currentTab || 'unknown',
    appVersion,
    userAgent: typeof navigator === 'undefined' ? '' : (navigator.userAgent || ''),
    windowSize: typeof window === 'undefined' ? '' : `${window.innerWidth}x${window.innerHeight}`,
    pageUrl: typeof window === 'undefined' ? '' : window.location.href,
    recentErrors: recentErrors(),
  };
}

export interface FeedbackResult {
  ok: boolean;
  /** What to show the user. The function supplies its own wording on success. */
  message: string;
  /** True when the session was the problem, so the UI can say "sign in again". */
  expired?: boolean;
}

export async function sendFeedback(
  sb: SupabaseClient | null,
  description: string,
  feedbackType: FeedbackTypeId,
  ctx: FeedbackContext,
): Promise<FeedbackResult> {
  if (!sb) return { ok: false, message: 'Not signed in.' };

  const { data: sess } = await sb.auth.getSession();
  const token = sess?.session?.access_token;
  if (!token) {
    // ⚠ NAMED SEPARATELY from a network failure. "Try again" is useless advice
    // when the fix is to sign in, and a person who retries three times and then
    // gives up is feedback lost.
    return { ok: false, message: 'Your session expired. Sign in again, then resend.', expired: true };
  }

  let res: Response;
  try {
    res = await fetch(`${ENV.config.supabaseUrl}/functions/v1/bug-report-submit`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        apikey: ENV.config.supabaseAnonKey,
      },
      body: JSON.stringify({ description, feedbackType, ...ctx }),
    });
  } catch {
    return { ok: false, message: 'Couldn’t reach the server. Check your connection and try again.' };
  }

  const out = await res.json().catch(() => ({})) as Record<string, unknown>;
  if (!res.ok) {
    return {
      ok: false,
      message: typeof out['error'] === 'string'
        ? out['error'] : 'Couldn’t send that. Try again in a moment.',
    };
  }
  return {
    ok: true,
    message: typeof out['message'] === 'string'
      ? out['message'] : 'Thanks — your feedback is in.',
  };
}
